import type { Component } from 'vue'
import {
  PhAirplaneTilt,
  PhBookOpenText,
  PhFileText,
  PhGlobeHemisphereEast,
  PhHandHeart,
  PhImage,
  PhLinkSimple,
  PhMicrophone,
  PhSparkle,
  PhTShirt,
  PhUsersThree,
  PhWaveform,
} from '@phosphor-icons/vue'

import logoSafwahSites from '@/assets/apps/safwah-sites.png'
import logoQuran from '@/assets/apps/quran.png'
import logoCarboneCopy from '@/assets/apps/carbone-copy.svg'
import logoNourulIlm from '@/assets/apps/nourul-ilm.png'
import logoRawResume from '@/assets/apps/raw-resume.png'
import logoRawThumbnails from '@/assets/apps/raw-thumbnails.png'
import logoAsSafwah from '@/assets/apps/as-safwah.png'

export type AppStatus = 'Live' | 'Building' | 'Soon'

export interface EcosystemApp {
  slug: string
  name: string
  tagline: string
  summary: string
  href: string
  segment: string
  status: AppStatus
  /** Short label shown in the UI */
  statusLabel: string
  icon: Component
  /** App mark; falls back to Phosphor icon when missing */
  logoUrl?: string
  tint: string
  /**
   * Monthly visitors / people who viewed (oldest → newest).
   * Prefer this until real revenue is ready to publish.
   */
  monthlyViews?: number[]
  /** Real monthly revenue in USD (oldest → newest). Only set when figures are accurate. */
  monthlyRevenue?: number[]
  seoTitle: string
  seoDescription: string
  audience: string
  highlights: string[]
  story: string[]
  faqs: Array<{ q: string; a: string }>
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

export const IN_SHA_ALLAH_AR = 'إن شاء الله'

export function isComingSoonStatus(status: AppStatus | string, statusLabel?: string) {
  if (status === 'Building' || status === 'Soon') return true
  return /coming soon/i.test(statusLabel ?? '')
}

/** Rolling 12 month labels ending with the current month. */
export function getMetricMonthLabels(count = 12): string[] {
  const now = new Date()
  const labels: string[] = []
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    labels.push(MONTH_SHORT[d.getMonth()])
  }
  return labels
}

/** @deprecated use getMetricMonthLabels */
export const getRevenueMonthLabels = getMetricMonthLabels

function formatCompactCount(n: number): string {
  if (n >= 1000) {
    const k = n / 1000
    const rounded = Math.round(k * 10) / 10
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}k`
  }
  return `${Math.round(n)}`
}

export function formatMonthlyViews(count: number): string {
  return `${formatCompactCount(count)} views/mo`
}

export function formatMonthlyRevenue(amount: number): string {
  return `$${formatCompactCount(amount)}/mo`
}

export function formatMetricAxis(n: number, kind: AppMetricKind): string {
  const compact = formatCompactCount(n)
  return kind === 'revenue' ? `$${compact}` : compact
}

export function formatMetricTooltip(n: number, kind: AppMetricKind): string {
  return kind === 'revenue' ? `$${formatCompactCount(n)}` : formatCompactCount(n)
}

export function getAppMetric(app: EcosystemApp): { kind: AppMetricKind; values: number[] } | null {
  if (app.monthlyRevenue?.length) return { kind: 'revenue', values: app.monthlyRevenue }
  if (app.monthlyViews?.length) return { kind: 'views', values: app.monthlyViews }
  return null
}

export const ecosystemApps: EcosystemApp[] = [
  {
    slug: 'safwah-sites',
    name: 'Safwah Sites',
    tagline: 'Websites that help local businesses get booked.',
    summary:
      'A web studio for local shops, clinics, and service businesses that need a clean site fast, without the agency maze.',
    href: 'https://safwahsites.com',
    segment: 'Web Studio',
    status: 'Live',
    statusLabel: 'Live',
    icon: PhGlobeHemisphereEast,
    logoUrl: logoSafwahSites,
    tint: 'bg-secondary',
    // Placeholder traffic — replace with real analytics when ready
    monthlyViews: [420, 680, 910, 1250, 1480, 1720, 2100, 2450, 2680, 3120, 3580, 4100],
    seoTitle: 'Safwah Sites | Websites for local businesses',
    seoDescription:
      'Safwah Sites builds clean, mobile-first websites for local businesses. Launch in about a week and start getting booked.',
    audience: 'Local business owners who want to look professional online and get more calls.',
    highlights: [
      'Mobile-first pages that load quickly',
      'Clear calls to action for bookings and inquiries',
      'Launch path measured in days, not months',
    ],
    story: [
      'Many small businesses still lose customers because their website looks abandoned or does not work on a phone. Safwah Sites exists to fix that with calm, practical design.',
      'We keep the process simple: understand your offer, design a clear homepage, ship a site people can trust, then help you take the next step.',
    ],
    faqs: [
      {
        q: 'Who is Safwah Sites for?',
        a: 'Local and booking-based businesses that need a trustworthy website without a long agency project.',
      },
      {
        q: 'How fast can a site launch?',
        a: 'Most projects are scoped to launch in about a week once content and direction are clear.',
      },
    ],
  },
  {
    slug: 'quran',
    name: 'As-Safwah Quran',
    tagline: 'Ad-free Quran reading for everyday worship.',
    summary:
      'A focused Mushaf experience with translation, tajweed support, audio, bookmarks, and offline reading.',
    href: 'https://quran.assafwahinc.com',
    segment: 'Worship',
    status: 'Live',
    statusLabel: 'Live',
    icon: PhBookOpenText,
    logoUrl: logoQuran,
    tint: 'bg-emerald-700',
    monthlyViews: [0, 40, 80, 120, 180, 210, 260, 310, 380, 420, 490, 560],
    seoTitle: 'As-Safwah Quran | Ad-free Quran reading app',
    seoDescription:
      'Read the Quran with translation, tajweed, audio, and bookmarks. An ad-free companion for daily worship.',
    audience: 'Muslims who want a calm, distraction-free Quran companion for daily reading.',
    highlights: [
      'Page-by-page Mushaf reading',
      'Translation, transliteration, and audio',
      'Bookmarks and offline-ready support',
    ],
    story: [
      'We wanted a Quran app that feels like sitting with the mushaf, not scrolling through ads and clutter.',
      'As-Safwah Quran keeps the interface quiet so your attention stays on the words of Allah.',
    ],
    faqs: [
      {
        q: 'Is it free of ads?',
        a: 'Yes. The reading experience is built to stay focused and free from advertising noise.',
      },
      {
        q: 'Can I use it offline?',
        a: 'The app is designed with offline-ready reading support so your practice can continue without constant connectivity.',
      },
    ],
  },
  {
    slug: 'carbone-copy',
    name: 'Carbone Copy',
    tagline: 'Turn one recording into a month of content.',
    summary:
      'Upload a podcast or video once and get a transcript plus draft packs for clips, blogs, social, newsletters, and YouTube metadata.',
    href: 'https://carbone-copy.com',
    segment: 'Creator Tools',
    status: 'Live',
    statusLabel: 'Live',
    icon: PhMicrophone,
    logoUrl: logoCarboneCopy,
    tint: 'bg-orange-800',
    monthlyViews: [180, 320, 540, 890, 1240, 1680, 2100, 1980, 2450, 2890, 3320, 3750],
    seoTitle: 'Carbone Copy | Repurpose recordings into content',
    seoDescription:
      'Carbone Copy turns one podcast or video into transcripts and ready drafts for social, blogs, newsletters, and YouTube.',
    audience: 'Creators and marketers who record once and need many publishable drafts fast.',
    highlights: [
      'Transcript plus multi-format draft packs',
      'Clip timestamps, social posts, and lead magnets',
      'Built for podcast and video workflows',
    ],
    story: [
      'Most recordings die after one post. Carbone Copy exists to stretch one session into a full content month without starting from a blank page.',
      'It is live today for creators who want speed with a clear, practical output set.',
    ],
    faqs: [
      {
        q: 'What do I upload?',
        a: 'A podcast episode or video recording. Carbone Copy returns a transcript and draft pack you can edit and publish.',
      },
      {
        q: 'Who is it for?',
        a: 'Solo creators, podcast hosts, and small teams who need consistent content without writing everything from scratch.',
      },
    ],
  },
  {
    slug: 'nourul-ilm',
    name: 'Nourul-Ilm',
    tagline: 'Daily Islamic reminders and halal productivity.',
    summary:
      'A link hub and content home for authentic deen reminders across YouTube, Instagram, TikTok, and Facebook.',
    href: 'https://nourul-ilm.com',
    segment: 'Media',
    status: 'Live',
    statusLabel: 'Live',
    icon: PhLinkSimple,
    logoUrl: logoNourulIlm,
    tint: 'bg-emerald-800',
    monthlyViews: [0, 0, 25, 40, 55, 70, 90, 110, 95, 130, 150, 175],
    seoTitle: 'Nourul-Ilm | Islamic reminders and content hub',
    seoDescription:
      'Nourul-Ilm shares daily Islamic reminders, halal productivity, and authentic deen content across major platforms.',
    audience: 'Muslims looking for clear, sincere reminders and a simple place to follow the content.',
    highlights: [
      'Central link-in-bio for all channels',
      'Focus on reminders and halal productivity',
      'Built for easy sharing and discovery',
    ],
    story: [
      'Nourul-Ilm gathers reminders and beneficial content so people can find the channels without hunting across apps.',
      'It is live as a clean front door for the project’s social presence.',
    ],
    faqs: [
      {
        q: 'What is Nourul-Ilm?',
        a: 'A content project sharing Islamic reminders and productivity guidance, with a simple hub site to reach every channel.',
      },
    ],
  },
  {
    slug: 'raw-resume',
    name: 'Raw Resume',
    tagline: 'Land the interview, not the trash pile.',
    summary:
      'AI-assisted resume building, ATS analysis, cover letters, and job matching designed to help candidates get hired faster.',
    href: 'https://raw-resume.com',
    segment: 'Career Tools',
    status: 'Live',
    statusLabel: 'Live',
    icon: PhFileText,
    logoUrl: logoRawResume,
    tint: 'bg-sky-800',
    monthlyViews: [90, 210, 480, 760, 1120, 1580, 2040, 2460, 2890, 3410, 3980, 4520],
    seoTitle: 'Raw Resume | AI resume and ATS tools',
    seoDescription:
      'Raw Resume helps job seekers with AI resume building, ATS analysis, cover letters, and job matching.',
    audience: 'Job seekers who need a sharper resume and clearer path to interviews.',
    highlights: [
      'Resume builder with ATS-minded guidance',
      'Cover letter generation',
      'Job matching support',
    ],
    story: [
      'Good candidates still get filtered out by weak formatting and vague applications. Raw Resume focuses on clarity that hiring systems and humans can both read.',
      'The product is live and shipping improvements for people who need results, not fluff.',
    ],
    faqs: [
      {
        q: 'Is Raw Resume only for tech jobs?',
        a: 'No. It is built for candidates across roles who want stronger applications and better interview odds.',
      },
    ],
  },
  {
    slug: 'raw-thumbnails',
    name: 'Raw Thumbnails',
    tagline: 'AI YouTube thumbnails and titles that earn the click.',
    summary:
      'Generate scroll-stopping thumbnail concepts and CTR-ready titles in one pass, with credit-based Creator and Studio plans.',
    href: 'https://raw-fortune.com',
    segment: 'Creator Tools',
    status: 'Live',
    statusLabel: 'Live',
    icon: PhImage,
    logoUrl: logoRawThumbnails,
    tint: 'bg-red-600',
    monthlyViews: [60, 140, 280, 520, 890, 1340, 1820, 1680, 2210, 2680, 3150, 3620],
    seoTitle: 'Raw Thumbnails | AI YouTube thumbnails and titles',
    seoDescription:
      'Raw Thumbnails creates YouTube thumbnail concepts and click-ready titles so creators stop guessing what earns attention.',
    audience: 'YouTube creators who need faster packaging decisions for thumbnails and titles.',
    highlights: [
      'Thumbnail concepts plus title options together',
      'Fast turnaround for packaging decisions',
      'Credit-based Creator and Studio plans',
    ],
    story: [
      'Thumbnails and titles decide whether a video gets a chance. Raw Thumbnails helps creators move from guesswork to clear options quickly.',
      'It is live for creators who want packaging help without a full design team.',
    ],
    faqs: [
      {
        q: 'What does Raw Thumbnails produce?',
        a: 'Thumbnail concepts and title options designed to improve click-through potential on YouTube.',
      },
    ],
  },
  {
    slug: 'scents',
    name: 'As-Safwah Scents',
    tagline: 'Alcohol-free attars inspired by Sunnah.',
    summary:
      'Story-driven oud and musk collections crafted for everyday elegance. In development and coming in the future إن شاء الله.',
    href: 'https://scents.as-safwah.com',
    segment: 'Lifestyle',
    status: 'Building',
    statusLabel: 'Coming soon',
    icon: PhSparkle,
    logoUrl: logoAsSafwah,
    tint: 'bg-amber-700',
    seoTitle: 'As-Safwah Scents | Alcohol-free Islamic attars',
    seoDescription:
      'As-Safwah Scents is in development. Alcohol-free attars inspired by Sunnah, coming in the future إن شاء الله.',
    audience: 'People who want fragrance that feels intentional, clean, and rooted in prophetic practice.',
    highlights: [
      'Alcohol-free formulations',
      'Sunnah-inspired scent stories',
      'Collections for daily wear and special moments',
    ],
    story: [
      'Scent is part of how we carry ourselves. As-Safwah Scents starts from that reminder and builds collections around care, cleanliness, and beauty.',
      'This brand is in development. We are building slowly so every bottle has a clear story and a clear standard before it opens.',
    ],
    faqs: [
      {
        q: 'When will Scents launch?',
        a: 'It is in development and will open when quality and supply are ready. Follow As-Safwah for updates.',
      },
      {
        q: 'Are the scents alcohol-free?',
        a: 'Yes. The collection is designed around alcohol-free attars.',
      },
    ],
  },
  {
    slug: 'attire',
    name: 'As-Safwah Attire',
    tagline: 'Modest luxury thobes, kufis, and essentials.',
    summary:
      'Premium modest wear with prophetic beauty and modern fit. In development and coming in the future إن شاء الله.',
    href: 'https://attire.assafwahinc.com',
    segment: 'Lifestyle',
    status: 'Building',
    statusLabel: 'Coming soon',
    icon: PhTShirt,
    logoUrl: logoAsSafwah,
    tint: 'bg-stone-800',
    seoTitle: 'As-Safwah Attire | Modest luxury thobes and essentials',
    seoDescription:
      'As-Safwah Attire is in development. Premium thobes, kufis, and modest essentials, coming in the future إن شاء الله.',
    audience: 'Men and families looking for modest clothing that feels refined without leaving tradition behind.',
    highlights: [
      'Thoughtful fabrics and modern fit',
      'Modest design with everyday usability',
      'Purpose woven into the brand model',
    ],
    story: [
      'Clothes shape how we show up for salah, work, and family. Attire is our way of making that choice feel easy and dignified.',
      'This brand is in development. We care about silhouette, comfort, and a quiet kind of excellence before we open the door.',
    ],
    faqs: [
      {
        q: 'When will Attire launch?',
        a: 'It is in development and coming in the future إن شاء الله. We will announce when collections are ready.',
      },
    ],
  },
  {
    slug: 'journeys',
    name: 'As-Safwah Journeys',
    tagline: 'Halal Hajj, Umrah, and Ummah travel.',
    summary:
      'Guided journeys that keep worship, comfort, and community at the center. In development and coming in the future إن شاء الله.',
    href: 'https://travel.as-safwah.com',
    segment: 'Travel',
    status: 'Building',
    statusLabel: 'Coming soon',
    icon: PhAirplaneTilt,
    logoUrl: logoAsSafwah,
    tint: 'bg-teal-700',
    seoTitle: 'As-Safwah Journeys | Halal Hajj and Umrah travel',
    seoDescription:
      'As-Safwah Journeys is in development. Halal Hajj, Umrah, and Ummah travel, coming in the future إن شاء الله.',
    audience: 'Travelers seeking trustworthy guidance for Hajj, Umrah, and meaningful Muslim travel.',
    highlights: [
      'Worship-first itinerary planning',
      'Support for groups and families',
      'Clear expectations before you leave home',
    ],
    story: [
      'Travel for worship should feel protected and clear. Journeys is being built to reduce confusion and keep the heart of the trip intact.',
      'This brand is in development and will open when care, partners, and guidance standards are ready.',
    ],
    faqs: [
      {
        q: 'When will Journeys open?',
        a: 'It is in development and coming in the future إن شاء الله. We will share dates when itineraries and support are ready.',
      },
    ],
  },
  {
    slug: 'media',
    name: 'The Safwah Project',
    tagline: 'Islamic media for family, mindset, and leadership.',
    summary:
      'Stories and shows that help Muslims grow with clarity and sincerity. In development and coming in the future إن شاء الله.',
    href: 'https://media.as-safwah.com',
    segment: 'Media',
    status: 'Building',
    statusLabel: 'Coming soon',
    icon: PhWaveform,
    logoUrl: logoAsSafwah,
    tint: 'bg-rose-800',
    seoTitle: 'The Safwah Project | Islamic media and storytelling',
    seoDescription:
      'The Safwah Project is in development. Islamic media on family, mindset, and leadership, coming in the future إن شاء الله.',
    audience: 'Viewers and listeners who want Islamic media that feels honest, useful, and family-aware.',
    highlights: [
      'Faith-forward storytelling',
      'Themes for family and personal growth',
      'Content that respects attention and sincerity',
    ],
    story: [
      'Media shapes culture. The Safwah Project aims to put useful Islamic conversation into formats people already watch and share.',
      'This brand is in development. We prefer clarity over noise and sincerity over performance.',
    ],
    faqs: [
      {
        q: 'When will the media hub launch?',
        a: 'It is in development and coming in the future إن شاء الله as episodes and formats mature.',
      },
    ],
  },
  {
    slug: 'foundation',
    name: 'As-Safwah Foundation',
    tagline: 'Transparent zakat and sadaqah with real impact.',
    summary:
      'Give toward masjid and relief work with clear reporting and trust. In development and coming in the future إن شاء الله.',
    href: 'https://foundation.as-safwah.com',
    segment: 'Giving',
    status: 'Building',
    statusLabel: 'Coming soon',
    icon: PhHandHeart,
    logoUrl: logoAsSafwah,
    tint: 'bg-green-800',
    seoTitle: 'As-Safwah Foundation | Transparent zakat and sadaqah',
    seoDescription:
      'As-Safwah Foundation is in development. Transparent zakat and sadaqah with real impact, coming in the future إن شاء الله.',
    audience: 'Donors who want their zakat and sadaqah handled with clarity and accountability.',
    highlights: [
      'Transparent reporting',
      'Masjid and relief priorities',
      'Giving that stays connected to community needs',
    ],
    story: [
      'Charity deserves trust. The Foundation is being built so donors can see where support goes and why it matters.',
      'This brand is in development. We treat every contribution as an amanah, so we will open when reporting is ready.',
    ],
    faqs: [
      {
        q: 'When will the Foundation open?',
        a: 'It is in development and coming in the future إن شاء الله. Transparency comes before launch.',
      },
    ],
  },
  {
    slug: 'ambassadors',
    name: 'As-Safwah Ambassadors',
    tagline: 'Partner with us through mosques and creators.',
    summary:
      'Campaigns, kits, and commissions for people who share the mission. Coming soon إن شاء الله.',
    href: 'https://influence.as-safwah.com',
    segment: 'Partners',
    status: 'Soon',
    statusLabel: 'Coming soon',
    icon: PhUsersThree,
    logoUrl: logoAsSafwah,
    tint: 'bg-slate-700',
    seoTitle: 'As-Safwah Ambassadors | Partner program for mosques and creators',
    seoDescription:
      'As-Safwah Ambassadors is coming soon إن شاء الله. Partner with mosques and creators to share the mission with integrity.',
    audience: 'Creators, mosque leaders, and community partners who want to collaborate with care.',
    highlights: [
      'Clear partner guidelines',
      'Campaign kits and support',
      'Commission structures that stay fair',
    ],
    story: [
      'Growth should not feel extractive. Ambassadors is for people who already serve their communities and want tools that match that trust.',
      'This program is coming soon إن شاء الله. We are building carefully before opening it widely.',
    ],
    faqs: [
      {
        q: 'When can partners apply?',
        a: 'The program is coming soon إن شاء الله. Reach out through Contact if you want to be notified early.',
      },
    ],
  },
]

export const liveApps = ecosystemApps.filter((app) => app.status === 'Live')
export const futureApps = ecosystemApps.filter((app) => app.status !== 'Live')

export function getAppBySlug(slug: string) {
  return ecosystemApps.find((app) => app.slug === slug)
}

export const siteNav = [
  { label: 'Home', to: '/' },
  { label: 'Companies', to: '/companies' },
  { label: 'Story', to: '/story' },
  { label: 'Mission', to: '/mission' },
] as const

/** Secondary links (footer / legal) — kept out of primary nav to avoid crowding */
export const footerNav = [
  { label: 'Contact', to: '/contact' },
  { label: 'Terms & Privacy', to: '/terms' },
] as const

export const SITE_ORIGIN = 'https://assafwahinc.com'

/** Circular seal — header, hero, and primary brand mark */
export const brandLogoUrl = new URL('../assets/logo/as-safwah-seal.png', import.meta.url).href

/** Serif wordmark — footer and secondary page identity */
export const brandWordmarkUrl = new URL('../assets/logo/as-safwah-wordmark.png', import.meta.url).href
